import { styled, css } from '../../modules';
import { keyframes } from 'styled-components';

export const PageWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    height: 100%;
    width: 100%;
    max-width: calc(100vh * 3 / 4);
    background-color: #ffffff;
    overflow: hidden;
`;

export const LogoSection = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    padding: 20px 24px;
    border-bottom: 1px solid #e5e5e5;
    flex-shrink: 0;
`;

export const LogoImage = styled.img`
    max-height: 52px;
    max-width: 200px;
    object-fit: contain;
`;

export const ScrollContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1;
    width: 100%;
    overflow-y: auto;
    padding: 28px 20px 24px;
    gap: 20px;

    /* In a constrained-height column flex container, children shrink by default
       and AccentCard (overflow: hidden) clips to a sliver. Keep natural heights
       and let the container scroll instead. */
    & > * {
        flex-shrink: 0;
    }
`;

// ── Page header ───────────────────────────────────────────

export const PageHeader = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

export const Eyebrow = styled.span`
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1.4px;
    color: #718096;
    text-transform: uppercase;
`;

export const Heading = styled.h1<{ $color: string }>`
    font-size: 23px;
    font-weight: 700;
    color: ${({ $color }) => $color};
    margin: 0;
    line-height: 1.2;
`;

export const Subtitle = styled.p`
    font-size: 14px;
    color: #718096;
    margin: 0;
    line-height: 1.5;
`;

// ── Troco hero ────────────────────────────────────────────

export const TrocoHero = styled.div<{ $color: string }>`
    width: 100%;
    background: linear-gradient(
        160deg,
        ${({ $color }) => $color}33 0%,
        #ffffff 75%
    );
    border: 1.5px solid ${({ $color }) => $color}55;
    border-radius: 16px;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

export const TrocoLead = styled.div<{ $color: string }>`
    display: flex;
    align-items: center;
    gap: 7px;
    color: ${({ $color }) => $color};
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.3px;
    text-transform: uppercase;
`;

export const TrocoValue = styled.div<{ $color: string }>`
    font-size: 38px;
    font-weight: 800;
    color: ${({ $color }) => $color};
    line-height: 1.05;
    font-variant-numeric: tabular-nums;
`;

export const TrocoCaption = styled.p`
    font-size: 13px;
    color: #4a5568;
    margin: 4px 0 0;
    line-height: 1.5;
`;

// ── Section caption ───────────────────────────────────────

export const Caption = styled.div`
    width: 100%;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.9px;
    color: #718096;
    text-transform: uppercase;
`;

// ── Accent card (RefinDetail) ─────────────────────────────

export const AccentCard = styled.div<{ $color: string }>`
    width: 100%;
    border: 1.5px solid ${({ $color }) => $color}44;
    border-radius: 14px;
    background: linear-gradient(
        180deg,
        ${({ $color }) => $color}11 0%,
        #ffffff 100%
    );
    overflow: hidden;
`;

export const CardHead = styled.div<{ $color: string }>`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 14px 16px;
    color: ${({ $color }) => $color};
    font-size: 16px;
    font-weight: 700;
    border-bottom: 1px solid ${({ $color }) => $color}22;
`;

// ── Math inset block ──────────────────────────────────────

export const MathInset = styled.div<{ $color: string }>`
    background: ${({ $color }) => $color}0D;
    border-bottom: 1px solid ${({ $color }) => $color}22;
    padding: 12px 16px;
    display: flex;
    flex-direction: column;
    gap: 0;
`;

export const MathRow = styled.div<{ $total?: boolean; $color: string }>`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: ${({ $total }) => ($total ? '10px 0 0' : '8px 0')};
    border-top: ${({ $total, $color }) => ($total ? `1px solid ${$color}33` : 'none')};
    margin-top: ${({ $total }) => ($total ? '2px' : '0')};
`;

export const MathLabel = styled.span<{ $total?: boolean; $color: string }>`
    font-size: ${({ $total }) => ($total ? '14px' : '13px')};
    font-weight: ${({ $total }) => ($total ? 700 : 400)};
    color: ${({ $total, $color }) => ($total ? $color : '#4a5568')};
    display: flex;
    align-items: center;
    gap: 5px;
`;

export const MathOp = styled.span<{ $color: string }>`
    font-size: 15px;
    font-weight: 700;
    color: ${({ $color }) => $color};
    opacity: 0.75;
    width: 14px;
    display: inline-flex;
    justify-content: center;
`;

export const MathValue = styled.span<{ $total?: boolean; $color: string }>`
    font-size: ${({ $total }) => ($total ? '18px' : '14px')};
    font-weight: 700;
    color: ${({ $total, $color }) => ($total ? $color : '#2d3748')};
    font-variant-numeric: tabular-nums;
`;

// ── Generic data rows (reused inside AccentCard and accordion) ─

export const DataRow = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 14px 16px;
    border-bottom: 1px solid #edf2f7;

    &:last-child {
        border-bottom: none;
    }
`;

export const RowLabel = styled.span`
    font-size: 14px;
    font-weight: 400;
    color: #718096;
    flex: 1;
    padding-right: 8px;
`;

export const RowValue = styled.span<{ $color: string }>`
    font-size: 14px;
    font-weight: 600;
    color: #2d3748;
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
`;

// ── Info callout (CCB link / payroll deduction) ───────────

export const InfoCallout = styled.div<{ $color: string }>`
    width: 100%;
    background-color: ${({ $color }) => $color}22;
    border-radius: 8px;
    border: 1px solid ${({ $color }) => $color}55;
    padding: 12px 14px;
    display: flex;
    flex-direction: row;
    gap: 10px;
    align-items: flex-start;
`;

export const IconWrapper = styled.div<{ $color: string }>`
    color: ${({ $color }) => $color};
    flex-shrink: 0;
`;

export const InfoText = styled.p<{ $color: string }>`
    font-size: 14px;
    color: #4a5568;
    margin: 0;
    line-height: 1.5;

    a {
        color: ${({ $color }) => $color};
        text-decoration: underline;
        cursor: pointer;
    }

    strong {
        font-weight: 700;
        color: #2d3748;
    }
`;

// ── Contract accordions ───────────────────────────────────

export const AccordionList = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

export const Accordion = styled.div<{ $open: boolean; $color: string }>`
    width: 100%;
    border: 1.5px solid ${({ $open, $color }) => ($open ? $color : '#edf2f7')};
    border-radius: 12px;
    overflow: hidden;
    transition: border-color 0.2s ease;
    background: ${({ $open, $color }) => ($open ? `${$color}08` : '#ffffff')};
`;

export const AccordionHead = styled.button`
    width: 100%;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 14px;
    border: none;
    background: transparent;
    cursor: pointer;
    text-align: left;
    font-family: inherit;
`;

export const AccordionIcon = styled.span<{ $color: string }>`
    width: 32px;
    height: 32px;
    border-radius: 9px;
    background: ${({ $color }) => $color}14;
    color: ${({ $color }) => $color};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`;

export const AccordionHeadLeft = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
`;

export const AccordionTitle = styled.span<{ $color: string }>`
    font-size: 14px;
    font-weight: 700;
    color: #2d3748;
    line-height: 1.25;
`;

export const AccordionSub = styled.span`
    font-size: 11.5px;
    color: #718096;
    font-variant-numeric: tabular-nums;
`;

export const AccordionHeadRight = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 1px;
    flex-shrink: 0;
`;

export const AccordionEmissionLabel = styled.span`
    font-size: 11px;
    color: #a0aec0;
    font-weight: 400;
`;

export const AccordionEmissionValue = styled.span<{ $color: string }>`
    font-size: 14px;
    font-weight: 700;
    color: ${({ $color }) => $color};
    font-variant-numeric: tabular-nums;
`;

export const AccordionChevron = styled.span<{ $open: boolean; $color: string }>`
    display: flex;
    align-items: center;
    flex-shrink: 0;
    color: ${({ $open, $color }) => ($open ? $color : '#a0aec0')};
    transition: transform 0.25s ease;
    transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'rotate(0deg)')};
`;

export const AccordionBody = styled.div<{ $open: boolean }>`
    display: grid;
    grid-template-rows: ${({ $open }) => ($open ? '1fr' : '0fr')};
    transition: grid-template-rows 0.3s ease;

    > .inner {
        overflow: hidden;
    }
`;

// ── Checkbox ──────────────────────────────────────────────

export const CheckboxWrapper = styled.div`
    width: 100%;
`;

// ── Footer ────────────────────────────────────────────────

export const Footer = styled.footer`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 16px 20px;
    border-top: 1px solid #e5e5e5;
    background-color: #ffffff;
    flex-shrink: 0;
`;

export const SubmitButton = styled.button<{ $backgroundColor: string; $disabled: boolean }>`
    width: 100%;
    padding: 16px 20px;
    border: none;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 600;
    color: #ffffff;
    background-color: ${({ $backgroundColor, $disabled }) => ($disabled ? '#cbd5e0' : $backgroundColor)};
    cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: opacity 0.2s;

    &:hover:not(:disabled) {
        opacity: 0.9;
    }
`;

// ── Bottom sheet (modal) ──────────────────────────────────

const fadeIn = keyframes`
    from { opacity: 0; }
    to   { opacity: 1; }
`;

const fadeOut = keyframes`
    from { opacity: 1; }
    to   { opacity: 0; }
`;

const slideUp = keyframes`
    from { transform: translateY(100%); }
    to   { transform: translateY(0); }
`;

const slideDown = keyframes`
    from { transform: translateY(0); }
    to   { transform: translateY(100%); }
`;

export const ModalOverlay = styled.div<{ $closing?: boolean }>`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: flex-end;
    justify-content: center;
    z-index: 100;
    ${({ $closing }) =>
        $closing
            ? css`animation: ${fadeOut} 0.3s ease forwards;`
            : css`animation: ${fadeIn} 0.3s ease forwards;`}
`;

export const ModalSheet = styled.div<{ $closing?: boolean }>`
    background: #ffffff;
    border-radius: 20px 20px 0 0;
    padding: 28px 24px 36px;
    width: 100%;
    max-width: calc(100vh * 3 / 4);
    max-height: 80vh;
    overflow-y: auto;
    overscroll-behavior: contain;
    display: flex;
    flex-direction: column;
    gap: 16px;
    ${({ $closing }) =>
        $closing
            ? css`animation: ${slideDown} 0.3s ease forwards;`
            : css`animation: ${slideUp} 0.3s ease forwards;`}
`;

export const SheetHandle = styled.div`
    width: 40px;
    height: 4px;
    border-radius: 2px;
    background-color: #ddd;
    align-self: center;
    margin-bottom: 4px;
    flex-shrink: 0;
`;

export const ModalTitle = styled.h2<{ $color: string }>`
    font-size: 20px;
    font-weight: 700;
    color: ${({ $color }) => $color};
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
    margin: 0;
`;

export const ModalText = styled.p`
    font-size: 14px;
    color: #4a5568;
    margin: 0;
    line-height: 1.6;
    text-align: center;

    ul {
        text-align: start;
    }
`;

export const ModalButtonRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 4px;
`;
